import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { next } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';

import type { Service, ValidationErrors } from 'webapp-types';
import { getInitials } from 'webapp/utils/common';
import { validateEmail } from 'webapp/utils/validate';

import didUpdate from 'webapp/modifiers/did-update';
import type { OptionRaw } from 'webapp/components/form/select';
import FormSelectComponent from 'webapp/components/form/select';
import NotificationsEditFormRowComponent from 'webapp/components/notifications/edit/form/row';
import RowErrorComponent from 'webapp/components/row-error';

import type { UserDataForList } from 'webapp/services/model-manager/users';

export interface ModelWithEmailRecipients {
	recipients: string[];
	errors?: ValidationErrors<string>;
}

interface Args {
	model: ModelWithEmailRecipients;
	submitInProgress: boolean;
	onContentChange: () => void;
	errors?: ValidationErrors<string>;
	theme?: string;
	isCustomAddDisabled?: boolean;
	excludeUserId?: number | null;
	showAvatar?: boolean;
	onClose?: () => void;
	onFocus?: () => void;
}

export default class FormEmailRecipientsComponent extends Component<Args> {
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('model-manager/users') declare mmUsersService: Service.ModelManager.Users;
	@service('current-user') declare currentUserService: Service.CurrentUser;

	@tracked recipientsOptions: OptionRaw<string>[] = [];

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.updateRecipientsOptions();
	}

	get errors(): ValidationErrors<string> {
		return this.args.errors || this.args.model.errors!;
	}

	// single source for the recipients row error state - multiple RowError components
	// on the same row would override each other's error class
	get recipientsErrorItems() {
		const errors = this.errors || {};
		return [...(errors.deliveryTypes || []), ...(errors.recipients || [])];
	}

	get isAgencyAdminInClientSpace() {
		const { isAdmin, isAgencyUserInClientSpace } = this.currentUserService;
		return isAdmin && isAgencyUserInClientSpace;
	}

	@cached
	get excludedUserRecord() {
		const excludeUserId = this.args.excludeUserId;
		return excludeUserId ? this.mmUsersService.findRecordQuick(String(excludeUserId)) : null;
	}

	@cached
	get userDataForList(): UserDataForList[] {
		const agencyAdminUserDataForList: UserDataForList[] = [];
		if (this.isAgencyAdminInClientSpace) {
			const currentUser = this.currentUserService.model;

			if (currentUser) {
				agencyAdminUserDataForList.push({
					value: currentUser.email,
					label: currentUser.name,
					label2: currentUser.email,
					avatar: currentUser.avatar,
					initials: getInitials(currentUser.name),
				});
			}
		}

		let list = [
			...agencyAdminUserDataForList,
			...this.mmUsersService.activatedUsersWithoutAgencyAdminsList,
		];

		const excludedUserRecord = this.excludedUserRecord;
		if (excludedUserRecord) {
			list = list.filter((user) => user.value !== excludedUserRecord.email);
		}

		return list;
	}

	updateRecipientsOptions(): void {
		const usersList: OptionRaw<string>[] = [];

		const showAvatar = !!this.args.showAvatar;

		this.userDataForList.forEach((userData: UserDataForList) => {
			usersList.push({
				value: userData.label2, //email
				label: userData.label || userData.label2, // name or email
				avatar: showAvatar ? userData.avatar : '',
				initials: showAvatar ? userData.initials : '',
			});
		});

		const excludedUserRecord = this.excludedUserRecord;
		this.args.model.recipients.forEach((email) => {
			if (
				!usersList.find((item) => item.value === email) &&
				excludedUserRecord?.email !== email
			) {
				usersList.push({
					value: email,
					label: email,
					initials: showAvatar ? getInitials(email) : '',
				});
			}
		});

		this.recipientsOptions = usersList;
	}

	@action
	onModelChange(): void {
		this.updateRecipientsOptions();
	}

	@action
	updateRecipients(value: string[]): void {
		this.args.model.recipients = value;
		next(() => {
			this.args.onContentChange();
		});
	}

	@action
	addCustomRecipient(value: string): boolean {
		const isValidEmail = validateEmail({ email: value }, 'email');
		if (isValidEmail) {
			this.recipientsOptions.push({
				value: value,
				label: value,
				initials: this.args.showAvatar ? getInitials(value) : '',
			});
			this.recipientsOptions = this.recipientsOptions;
		} else {
			this.infolayerService.error(`Invalid email: ${value}`);
		}
		return isValidEmail;
	}
	<template>
		<NotificationsEditFormRowComponent
			@label="Recipient(s)"
			@className="row--dropdown row--recipients"
		>
			<FormSelectComponent
				name="recipients"
				@selected={{@model.recipients}}
				@options={{this.recipientsOptions}}
				@isNumeric={{false}}
				@isMultiple={{true}}
				@placeholder="Enter recipient emails"
				@isDisabled={{@submitInProgress}}
				@tooltipOnSelectionFromProperty=""
				@onChange={{this.updateRecipients}}
				@onCustomAdd={{unless @isCustomAddDisabled this.addCustomRecipient}}
				@customAddCaseInsensitive={{true}}
				@showFloatlabel={{true}}
				@onFocus={{@onFocus}}
				@onClose={{@onClose}}
				@theme={{@theme}}
			/>
			<RowErrorComponent @items={{this.recipientsErrorItems}} />
			<span {{didUpdate this.onModelChange @model}}></span>
		</NotificationsEditFormRowComponent>
	</template>
}
